import { useCallback, useRef } from 'react';
import { useAuth } from './useAuth';

export interface UseProtectedFetchOptions {
  /**
   * Base URL for API requests
   * @example 'http://localhost:5000'
   */
  baseUrl?: string;

  /**
   * Callback when request is unauthorized (401)
   */
  onUnauthorized?: () => void;

  /**
   * Retry request on 401 error after token refresh
   * @default true
   */
  retryOn401?: boolean;

  /**
   * Additional headers to include in all requests
   */
  headers?: Record<string, string>;
}

export interface UseProtectedFetchReturn {
  /**
   * Fetch with automatic token injection and JSON parsing
   *
   * @param url - URL or path to fetch
   * @param init - Fetch init options
   * @returns Parsed JSON response
   */
  fetchJson: <T = any>(url: string, init?: RequestInit) => Promise<T>;

  /**
   * Raw fetch with automatic token injection
   *
   * @param url - URL or path to fetch
   * @param init - Fetch init options
   * @returns Fetch response
   */
  fetch: (url: string, init?: RequestInit) => Promise<Response>;

  /**
   * Check if request is in progress
   */
  isLoading: boolean;
}

/**
 * Hook for making authenticated API requests
 *
 * Automatically injects the current user's access token into all requests.
 * Handles 401 errors and provides convenient methods for JSON APIs.
 *
 * @param options - Configuration options
 * @returns Protected fetch methods
 *
 * @example Basic usage
 * ```tsx
 * import { useProtectedFetch } from 'antaeus.keycloak.react';
 *
 * function MyComponent() {
 *   const { fetchJson } = useProtectedFetch({
 *     baseUrl: 'http://localhost:5000'
 *   });
 *
 *   const loadData = async () => {
 *     // Token is automatically added
 *     const data = await fetchJson('/api/data');
 *     console.log(data);
 *   };
 *
 *   return <button onClick={loadData}>Load Data</button>;
 * }
 * ```
 *
 * @example With custom headers
 * ```tsx
 * const { fetchJson } = useProtectedFetch({
 *   baseUrl: 'http://localhost:5000',
 *   headers: {
 *     'X-Custom-Header': 'value'
 *   }
 * });
 * ```
 *
 * @example POST request
 * ```tsx
 * const { fetchJson } = useProtectedFetch({
 *   baseUrl: 'http://localhost:5000'
 * });
 *
 * const createUser = async (userData: User) => {
 *   const result = await fetchJson<User>('/api/users', {
 *     method: 'POST',
 *     body: JSON.stringify(userData),
 *     headers: {
 *       'Content-Type': 'application/json'
 *     }
 *   });
 *   return result;
 * };
 * ```
 *
 * @example Handle unauthorized
 * ```tsx
 * const { fetchJson } = useProtectedFetch({
 *   baseUrl: 'http://localhost:5000',
 *   onUnauthorized: () => {
 *     console.log('Unauthorized! Redirecting to login...');
 *   }
 * });
 * ```
 */
export function useProtectedFetch(
  options: UseProtectedFetchOptions = {}
): UseProtectedFetchReturn {
  const auth = useAuth();
  const refreshInProgressRef = useRef(false);

  // Extract only what we need to avoid unnecessary re-renders
  const { user, isLoading, signinSilent } = auth;

  const {
    baseUrl = '',
    onUnauthorized,
    retryOn401 = true,
    headers: customHeaders = {},
  } = options;

  /**
   * Build full URL from path
   */
  const buildUrl = useCallback(
    (url: string): string => {
      // If URL is already absolute, return as-is
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }

      // Combine with base URL
      const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const path = url.startsWith('/') ? url : `/${url}`;

      return base + path;
    },
    [baseUrl]
  );

  /**
   * Get authorization header with current token
   * Note: Not memoized to always get fresh token
   */
  const getAuthHeader = (): Record<string, string> => {
    if (!user?.access_token) {
      return {};
    }

    return {
      Authorization: `Bearer ${user.access_token}`,
    };
  };

  /**
   * Merge all headers
   * Always gets fresh token by calling getAuthHeader() directly
   */
  const buildHeaders = useCallback(
    (requestHeaders?: HeadersInit): Headers => {
      const headers = new Headers(requestHeaders);

      // Add auth header (always fresh)
      const authHeader = getAuthHeader();
      if (authHeader.Authorization) {
        headers.set('Authorization', authHeader.Authorization);
      }

      // Add custom headers
      Object.entries(customHeaders).forEach(([key, value]) => {
        headers.set(key, value);
      });

      return headers;
    },
    [customHeaders]
  );

  /**
   * Raw fetch with token injection
   */
  const protectedFetch = useCallback(
    async (url: string, init?: RequestInit): Promise<Response> => {
      const fullUrl = buildUrl(url);
      const headers = buildHeaders(init?.headers);

      const response = await fetch(fullUrl, {
        ...init,
        headers,
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        onUnauthorized?.();

        // Only retry if enabled, user exists, and refresh not already in progress
        if (retryOn401 && user && !refreshInProgressRef.current) {
          // Set flag to prevent concurrent refresh attempts
          refreshInProgressRef.current = true;

          try {
            // Try to refresh token silently
            await signinSilent();

            // Retry request with new token
            const newHeaders = buildHeaders(init?.headers);
            const retryResponse = await fetch(fullUrl, {
              ...init,
              headers: newHeaders,
            });

            return retryResponse;
          } catch (error) {
            // Silent refresh failed, return original response
            return response;
          } finally {
            // Reset flag after delay to allow future refreshes
            setTimeout(() => {
              refreshInProgressRef.current = false;
            }, 1000);
          }
        }
      }

      return response;
    },
    [buildUrl, buildHeaders, onUnauthorized, retryOn401, user, signinSilent]
  );

  /**
   * Fetch and parse JSON
   */
  const fetchJson = useCallback(
    async <T = any>(url: string, init?: RequestInit): Promise<T> => {
      const response = await protectedFetch(url, init);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP ${response.status}: ${response.statusText}. ${errorText}`
        );
      }

      const data = await response.json();
      return data as T;
    },
    [protectedFetch]
  );

  return {
    fetchJson,
    fetch: protectedFetch,
    isLoading,
  };
}
